import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import { IActivo, IReporteBase } from 'modelos/src';

/**
 * Interface para ambulancias con reporte
 */
export interface IAmbulanciaConReporte extends IActivo {
  ultimoReporte?: IReporteBase<'Tracker 4G'>;
  color?: string;
  asignada?: boolean;
}

/**
 * AmbulanciasLayerManager
 *
 * Manager para renderizar ambulancias en el mapa.
 *
 * Características:
 * - Icono simple de pin (varía según asignación)
 * - Sin clustering (las ambulancias no se agrupan)
 * - Iconos SVG (60x60px): ambulancia.svg (disponible), ambulancia_asignada.svg (asignada)
 *
 * Tipos de reportes soportados:
 * - Tracker 4G
 */
@Injectable({
  providedIn: 'root',
})
export class AmbulanciasLayerManager extends BaseEntityLayerManager<IAmbulanciaConReporte> {
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'ambulancias';
  readonly supportsClustering = false; // Las ambulancias no se agrupan
  readonly clusterColor = '#F44336'; // No usado, pero requerido por la base

  // IDs de layers específicos
  private mainLayerId = 'ambulancias-layer';
  private textLayerId = 'ambulancias-text-layer';

  // Variable para tracking del ID seleccionado
  private idAmbulanciaSeleccionada: string | null = null;

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte una ambulancia a un Feature de GeoJSON
   */
  override entityToFeature(
    ambulancia: IAmbulanciaConReporte,
  ): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(ambulancia);

    // Extraer coordenadas del último reporte
    const valores = ambulancia.ultimoReporte?.valores;
    const geojson = valores?.geojson;
    const coordinates: [number, number] = geojson?.coordinates || [
      -58.443832, -34.587386,
    ];

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties: {
        id: ambulancia._id,
        asignada: ambulancia.asignada || false,
        // Metadata adicional para popups
        funcion: ambulancia.funcion,
        color: ambulancia.color,
        velocidad: valores?.velocidad,
        fechaReporte: ambulancia.ultimoReporte?.fechaCreacion,
        // Datos completos para popup
      },
    };
  }

  /**
   * Expresión para el icono de ambulancia (varía según asignación)
   */
  override getIconExpression(): mapboxgl.Expression {
    return [
      'case',
      ['get', 'asignada'],
      'ambulancia-asignada-icon',
      'ambulancia-no-asignada-icon',
    ];
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea el layer para ambulancias
   *
   * Las ambulancias usan SVGs de pin completo (ambulancia.svg, ambulancia_asignada.svg)
   * No necesitan sistema de dos capas como los vehículos/colectivos
   */
  protected createLayers(): void {
    if (!this.map) return;

    // Layer principal de ambulancias
    this.addLayer({
      id: this.mainLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        visibility: 'visible',
        'icon-image': this.getIconExpression(),
        'icon-size': 0.583, // SVG 60px → 35px (35/60 = 0.583)
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'icon-rotation-alignment': 'viewport',
        'icon-pitch-alignment': 'viewport',
        'icon-anchor': 'bottom', // Anclar en la parte inferior como pins
      },
    });

    // Text layer para mostrar identificación
    this.addLayer({
      id: this.textLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'text-field': ['get', 'id'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-anchor': 'top',
        'text-offset': [0, 1.5],
        'text-allow-overlap': false,
        'text-ignore-placement': false,
        visibility: 'none', // Inicialmente oculto
      },
      paint: {
        'text-color': '#000000',
        'text-halo-color': '#FFFFFF',
        'text-halo-width': 2,
      },
    });
  }

  // ========== VISIBILIDAD GENERAL ==========

  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    if (this.map.getLayer(this.mainLayerId)) {
      this.map.setLayoutProperty(
        this.mainLayerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  // ========== MOSTRAR NOMBRE ==========

  /**
   * Muestra u oculta el nombre de una ambulancia específica
   * @param ambulanciaId ID de la ambulancia o null para ocultar todos los nombres
   */
  public mostrarNombreDeAmbulancia(ambulanciaId: string | null): void {
    if (!this.map || !this.map.getLayer(this.textLayerId)) return;

    this.idAmbulanciaSeleccionada = ambulanciaId;

    if (ambulanciaId === null) {
      // Ocultar todos los nombres
      this.map.setLayoutProperty(this.textLayerId, 'visibility', 'none');
      this.map.setFilter(this.textLayerId, null);
    } else {
      // Mostrar solo el nombre de la ambulancia seleccionada
      this.map.setLayoutProperty(this.textLayerId, 'visibility', 'visible');
      this.map.setFilter(this.textLayerId, ['==', ['get', 'id'], ambulanciaId]);
    }
  }
}
